import { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical, Plus, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { SortableItemProps, VerificationItem } from '../types';
import { AddItemDialog } from './add-item-dialog';
import { FdicCheckComponent } from './fdic-check';
import { GleifCheckComponent } from './gleif-check';
import { OFACCheckComponent } from './ofac-check';
import { SignableNoteComponent } from './signable-note';

interface EditableNoteTitleProps {
  title: string;
  onSave: (title: string) => void;
}

function EditableNoteTitle({ title, onSave }: EditableNoteTitleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (editValue.trim()) {
      onSave(editValue);
    } else {
      setEditValue(title); // Reset to original if empty
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        className="h-7 text-base font-semibold"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSave();
          } else if (e.key === 'Escape') {
            setEditValue(title);
            setIsEditing(false);
          }
        }}
        onBlur={handleSave}
      />
    );
  }

  return (
    <div
      className="group/title flex cursor-pointer items-center gap-2"
      onClick={() => setIsEditing(true)}
    >
      <span>{title}</span>
      <Pencil className="size-3.5 opacity-0 transition-opacity group-hover/title:opacity-100" />
    </div>
  );
}

const CHECK_TITLE_PREFIX: Record<'ofac' | 'fdic' | 'gleif', string> = {
  ofac: 'OFAC Check',
  fdic: 'FDIC Check',
  gleif: 'GLEIF Check',
};

export function SortableItem({
  id,
  item,
  onVerifyOFAC,
  onVerifyFDIC,
  onVerifyGLEIF,
  onSelectGleifNameSearchResult,
  onResetGleif,
  onSetGleifNotApplicable,
  onUpdateNoteContent,
  onUpdateNoteTitle,
  onUpdateEntityName,
  onUpdateCertNumber,
  onUpdateLei,
  onSignNote,
  onDelete,
  isSearching,
  userId,
  items,
  setItems,
  deal,
  isAnyDragging,
  onConvertNoteToGleif,
  convertingNoteId,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleAddItem = (newItem: VerificationItem, position: 'above' | 'below') => {
    const currentIndex = items.findIndex((i) => i.id === id);
    const newItems = [...items];
    newItems.splice(position === 'above' ? currentIndex : currentIndex + 1, 0, newItem);
    setItems(newItems);
  };

  const renderItemBody = () => {
    if (item.type === 'ofac') {
      return (
        <OFACCheckComponent
          item={item}
          onVerify={onVerifyOFAC}
          onSign={onSignNote}
          onUpdateEntityName={onUpdateEntityName}
          isSearching={isSearching}
          userId={userId}
        />
      );
    }
    if (item.type === 'fdic') {
      return (
        <FdicCheckComponent
          item={item}
          onVerify={onVerifyFDIC}
          onSign={onSignNote}
          onUpdateEntityName={onUpdateEntityName}
          onUpdateCertNumber={onUpdateCertNumber}
          isSearching={isSearching}
          userId={userId}
        />
      );
    }
    if (item.type === 'gleif') {
      return (
        <GleifCheckComponent
          item={item}
          onVerify={onVerifyGLEIF}
          onSelectNameSearchResult={onSelectGleifNameSearchResult}
          onReset={onResetGleif}
          onSetNotApplicable={onSetGleifNotApplicable}
          onSign={onSignNote}
          onUpdateEntityName={onUpdateEntityName}
          onUpdateLei={onUpdateLei}
          isSearching={isSearching}
          userId={userId}
        />
      );
    }
    return (
      <SignableNoteComponent
        item={item}
        onUpdateContent={onUpdateNoteContent}
        onSign={onSignNote}
        userId={userId}
        deal={deal}
        onConvertToGleif={onConvertNoteToGleif}
        isConverting={convertingNoteId === item.id}
      />
    );
  };

  return (
    <div ref={setNodeRef} style={style} className="group relative pl-8">
      <TooltipProvider>
        <div className="absolute left-0 top-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <AddItemDialog
                  onAddItem={(newItem) => handleAddItem(newItem, 'above')}
                  trigger={(
                    <Button
                      variant="ghost"
                      size="sm"
                      className="size-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <Plus className="size-3" />
                    </Button>
                  )}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Add Above</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="absolute bottom-0 left-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <AddItemDialog
                  onAddItem={(newItem) => handleAddItem(newItem, 'below')}
                  trigger={(
                    <Button
                      variant="ghost"
                      size="sm"
                      className="size-6 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <Plus className="size-3" />
                    </Button>
                  )}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Add Below</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
      <Card className="mb-4">
        <CardHeader className="flex flex-row items-center gap-4">
          <div {...attributes} {...listeners}>
            <GripVertical className="size-4 cursor-move text-muted-foreground" />
          </div>
          <div className="flex flex-1 items-center justify-between">
            <CardTitle className="text-base">
              {item.type === 'ofac' || item.type === 'fdic' || item.type === 'gleif'
                ? `${CHECK_TITLE_PREFIX[item.type]} - ${item.entityName || 'Unnamed Entity'}`
                : (
                    <EditableNoteTitle
                      title={item.title}
                      onSave={(newTitle) => onUpdateNoteTitle(id, newTitle)}
                    />
                  )}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(id)}
            >
              <X className="size-3" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className={cn(
          'transition-all duration-200',
          isAnyDragging && 'max-h-0 opacity-0 overflow-hidden p-0',
          !isAnyDragging && 'max-h-[2000px]',
        )}
        >
          {renderItemBody()}
        </CardContent>
      </Card>
    </div>
  );
}
